import { DeliveryType } from "@/types/IOrder"


export interface IAnalyticsByCustomerParams {
  optionsList: IAnalyticsByCustomerParamsOptions[]
}

export interface IAnalyticsParams {
  optionsList: IAnalyticsParamsOptions[]
}

export interface IAnalyticsByCustomerParamsOptions {
  activePeriod?: AnalyticsPeriod
  startDate?: string
  endDate?: string
  fuelTypeId?: number
  depotId?: number
}

export interface IAnalyticsParamsOptions {
  activePeriod?: AnalyticsPeriod
  startDate?: string
  endDate?: string
  fuelTypeId?: number
  depotId?: number
  customerClientId?: number
  customerCategoryId?: number
}

export interface IOrderAnalytics {
  period: string
  orderCount: number
  orderAmount: number
  orderSum: number
  dataId?: string 
}

export interface ISalesAnalytics extends IOrderAnalytics{
  comparisonData?: ISalesAnalytics[]
}

export interface BaseDiagramAnalytics {
  rowKey: string
  period: string
  filterText?: string
}

export interface OrderDiagramAnalytics extends BaseDiagramAnalytics {
  orderCount: number
  orderAmount: number
  orderSum: number
  orderSumMillions: number
  dataId?: string
}

export interface SalesDiagramAnalytics extends OrderDiagramAnalytics {
  comparisonData?: SalesDiagramAnalytics[]
}

export interface IPriceAnalytics extends BaseDiagramAnalytics {
  fuels: PriceAnalyticsItem[]
}

interface PriceAnalyticsItem {
  price: number
  fuelTypeName: string
}

export interface IRefusalAnalytics extends BaseDiagramAnalytics {
  refusalCount: number
  refusalAmount: number
  orderCount: number
  orderAmount: number
  orderSum: number
  orderSumMillions: number
  refusalSum: number
  refusalSumMillions: number
}

export interface IDeliveriesPickupsAnalytics {
  period: string
  deliveryCount: number
  deliveryAmount: number
  deliverySum: number
  pickupCount: number
  pickupAmount: number
  pickupSum: number
}

export interface IDeliveriesPickupsDiagramAnalytics extends BaseDiagramAnalytics, IDeliveriesPickupsAnalytics {
  deliverySumMillions: number
  pickupSumMillions: number
}

export interface ILimitsAnalytics extends BaseDiagramAnalytics {
  limits: ILimitsAnalyticsItem[]
}

export interface ILimitsAnalyticsItem {
  limit: number
  fuelTypeName: string
}

export interface IOpeningSaleDifferanceAnalytics extends BaseDiagramAnalytics {
  items: IOpeningSaleDifferanceAnalyticsItem[]
}

export interface IOpeningSaleDifferanceAnalyticsItem {
  period: string
  open: number
  sale: number
  fuelTypeName: string
}

export interface IOpeningSaleDifferanceDiagramAnalytics {
  period: string
  open: number
  sale: number
  fuelTypeName: string
}

export interface ILimitsDiagramAnalytics {
  period: string
  limit: number
  fuelTypeName: string
}

export interface IComparisonDiagramAnalytics {
  period: string
  mainValue?: number
  additionalValue?: number
}

export interface IAnalyticsOptions {
  depotId: number | undefined
  fuelTypeId: number | undefined
  startDate: Date | undefined
  endDate: Date | undefined
  period: AnalyticsPeriod | undefined
  customerId?: number
}

export interface IAnalyticsRefusal {
  period: string
  refusalCount: number
  refusalAmount: number
  orderCount: number
  orderAmountTon: number
}

export interface ComparisonPeriod {
  startDate?: Date
  endDate?: Date
  periodText?: string
}

export enum AnalyticsPeriod {
  Daily,
  Weekly,
  Monthly,
  Quarterly,
  Yearly
}

export interface IAnalyticsPageDisplayUnit {
  value: string
  label: string
  bigLabel: string
  optionsLabel?: string | null | undefined
}

export enum BaseDisplayUnit {
  OrderAmount,
  OrderSum,
  OrderSumMillions,
  LimitSales,
  OrderCount
}

export const baseDisplayUnitsMap = {
  [BaseDisplayUnit.OrderAmount]: {
    value: "orderAmount",
    label: "Тонны",
    bigLabel: "Объем заказов (тонн)"
  },
  [BaseDisplayUnit.OrderSum]: {
    value: "orderSum",
    label: "Рубли",
    bigLabel: "Сумма заказов"
  },
  [BaseDisplayUnit.OrderSumMillions]: {
    value: "orderSumMillions",
    label: "Рубли (млн)",
    bigLabel: "Сумма заказов (млн)"
  },
  [BaseDisplayUnit.LimitSales]: {
    value: "limitSales",
    label: "Тонны",
    bigLabel: "Разница"
  },
  [BaseDisplayUnit.OrderCount]: {
    value: "orderCount",
    label: "Количество",
    bigLabel: "Количество заказов"
  }
}

export const diagramColors = [
  "#8884d8",
  "#82ca9d",
  "#F39F18",
  "#008F39",
  "#7FB5B5",
  "#781F19",
  "#1E1E1E",
  "#49678D",
  "#D36E70",
  "#89AC76",
  "#1E2460",
  "#D0D0D0"
]
export enum FilterType {
  Period,
  DeliveryType,
  Fuel,
  Depot,
  Customer,
  CustomerCategory
}

export const BaseFilterTypes = [
  FilterType.Period,
  FilterType.Fuel,
  FilterType.Depot
]

export const BaseFilterTypesForAdmin = [
  ...BaseFilterTypes,
  FilterType.Customer,
  FilterType.CustomerCategory
]

export const AnalyticsFilterPeriods = [
  {
    label: "Год",
    value: AnalyticsPeriod.Yearly
  },
  {
    label: "Квартал",
    value: AnalyticsPeriod.Quarterly
  },
  {
    label: "Месяц",
    value: AnalyticsPeriod.Monthly
  },
  {
    label: "Неделя",
    value: AnalyticsPeriod.Weekly
  },
  {
    label: "День",
    value: AnalyticsPeriod.Daily
  }
]

export const AnalyticsDeliveryTypes = [
  {
    label: "Все",
    value: DeliveryType.All
  },
  {
    label: "Доставка",
    value: DeliveryType.Delivery
  },
  {
    label: "Самовывоз",
    value: DeliveryType.Pickup
  }
]

export interface IAnalyticsSettingsOptions {
  depotId: number | undefined
  fuelTypeId: number | undefined
  startDate: string | undefined
  endDate: string | undefined
  activePeriod: AnalyticsPeriod | undefined
  customerClientId: number | undefined
  customerCategoryId: number | undefined
  deliveryType: DeliveryType
  depotName: string | undefined
  depotDisabled?: boolean
  fuelTypeName: string | undefined
  activePeriodName: string | undefined
  customerClientName: string | undefined
  customerCategoryName: string | undefined
  deliveryTypeName: string | undefined
}

export interface AnalyticsOptionValue {
  value: number | undefined,
  label: string
}

export const AnalyticsDefaultOptions: IAnalyticsSettingsOptions = {
  depotId: undefined,
  fuelTypeId: undefined,
  startDate: undefined,
  endDate: undefined,
  activePeriod: AnalyticsPeriod.Monthly,
  customerClientId: undefined,
  customerCategoryId: undefined,
  deliveryType: DeliveryType.All,

  fuelTypeName: undefined,
  depotName: undefined,
  depotDisabled: false,
  customerCategoryName: undefined,
  customerClientName: undefined,
  activePeriodName: AnalyticsFilterPeriods.find(p => p.value === AnalyticsPeriod.Monthly)?.label,
  deliveryTypeName: AnalyticsDeliveryTypes.find(d => d.value === DeliveryType.All)?.label
}

/*
для обогащения deepCopy options
*/
export const AnalyticsEmptyOptions: IAnalyticsSettingsOptions = {
  depotId: undefined,
  fuelTypeId: undefined,
  startDate: undefined,
  endDate: undefined,
  activePeriod: undefined,
  customerClientId: undefined,
  customerCategoryId: undefined,
  deliveryType: DeliveryType.All,

  fuelTypeName: undefined,
  depotName: undefined,
  depotDisabled: false,
  customerCategoryName: undefined,
  customerClientName: undefined,
  activePeriodName: undefined,
  deliveryTypeName: AnalyticsDeliveryTypes.find(d => d.value === DeliveryType.All)?.label
}

export interface AnalyticsChartProps {
  data: any[] | undefined
  dataKeys: IAnalyticsPageDisplayUnit[]
  colors?: string[]
  filtersText?: string | string[] | undefined | null
  tooltipContent?: any
  showDots?: boolean
}